/**
 * Branding provider: fetches /v1/branding on mount and writes the
 * resolved tokens as CSS custom properties on document.documentElement.
 *
 * The provider is mounted once in main.tsx, above the route table.
 * Tenant changes (e.g. user picks a different active tenant in S14
 * tenant-picker) call the exposed ``refresh`` to re-read.
 */

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { loadConfig } from "../api/client";

type BrandingTokens = Record<string, string | null | undefined | Record<string, string>>;

interface BrandingState {
  loading: boolean;
  tokens: BrandingTokens;
}

interface BrandingContextValue extends BrandingState {
  refresh: () => Promise<void>;
}

const BrandingContext = createContext<BrandingContextValue | null>(null);

const COLOUR_TOKEN_TO_CSS_VAR: Record<string, string> = {
  primary_colour: "--brand-primary",
  accent_colour: "--brand-accent",
  surface_colour: "--brand-surface",
};

function applyTokens(tokens: BrandingTokens): void {
  const root = document.documentElement;
  for (const [tokenName, cssVar] of Object.entries(COLOUR_TOKEN_TO_CSS_VAR)) {
    const value = tokens[tokenName];
    if (typeof value === "string" && value.length > 0) {
      root.style.setProperty(cssVar, value);
    } else {
      root.style.removeProperty(cssVar);
    }
  }
  // Title and favicon: optional, treat absence as "leave default".
  const productName = (tokens.product_name ?? tokens.display_name) as string | undefined;
  if (typeof productName === "string" && productName.length > 0) {
    document.title = productName;
  }
  const faviconUrl = tokens.favicon_url;
  if (typeof faviconUrl === "string" && faviconUrl.length > 0) {
    let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
    if (link === null) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = faviconUrl;
  }
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<BrandingState>({
    loading: true,
    tokens: {},
  });

  const refresh = useCallback(async () => {
    try {
      const cfg = loadConfig();
      const response = await fetch(`${cfg.baseUrl}/v1/branding`, {
        credentials: "include",
      });
      if (!response.ok) {
        setState({ loading: false, tokens: {} });
        return;
      }
      const tokens = (await response.json()) as BrandingTokens;
      applyTokens(tokens);
      setState({ loading: false, tokens });
    } catch {
      setState({ loading: false, tokens: {} });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<BrandingContextValue>(
    () => ({ ...state, refresh }),
    [state, refresh],
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): BrandingContextValue {
  const value = useContext(BrandingContext);
  if (value === null) {
    throw new Error("useBranding must be used inside a <BrandingProvider>");
  }
  return value;
}
